import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { SsoService, DirectLoggedOut, getDirectLoggedIn } from '@uxdf/direct-shared-lib';
import { Store, select } from '@ngrx/store';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { MenuItem, SSO_CODES } from '../menu-item.model';

@Component({
  standalone: false,
  selector: 'app-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.scss'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [style({ height: 0, marginTop: 0 }), animate(300, style({ height: '*', marginTop: '*' }))]),
      transition(':leave', [style({ height: '*', marginTop: '*' }), animate(300, style({ height: 0, marginTop: 0 }))])
    ]),
    trigger('panelToggle', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate(200, style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate(200, style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class MobileMenuComponent implements OnInit, OnDestroy {
  @Input() opened = false;
  @Output() closed = new EventEmitter<void>();

  submenuOpened: boolean;
  loggedIn$: Observable<boolean>;
  menuItems: MenuItem[] = [];
  externalLinks: MenuItem[] = [];
  faqString = $localize`:@@mobile-menu.FAQ:FAQ`;
  logOutString = $localize`:@@mobile-menu.commonLogoutText:Log out`;
  landingSignInString = $localize`:@@mobile-menu.landingSignIn:Sign in`;
  public readonly ssoCodes = SSO_CODES;

  private routerSubscription: Subscription;

  constructor(
    private readonly ssoService: SsoService,
    public readonly store: Store<any>,
    private readonly router: Router
  ) {
    this.loggedIn$ = this.store.pipe(select(getDirectLoggedIn));
  }

  @Input()
  set items(items: MenuItem[]) {
    if (items) {
      this.menuItems = this.prepareMenuItems(items);
      this.submenuOpened = !!this.menuItems.find(({ opened }) => true === opened);
    }
  }

  @Input() set bottomMenu(items: MenuItem[]) {
    if (items) {
      this.externalLinks = this.prepareExternalLinks(items);
    }
  }

  ngOnInit() {
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.menuItems = this.menuItems.map(item => ({
        ...item,
        opened: item.children.length && this.router.isActive(item.link, false)
      }));
      this.submenuOpened = !!this.menuItems.find(({ opened }) => true === opened);
      this.close();
    });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  navigateBySSOLink(page: string) {
    this.ssoService.redirectToSupportPage(page);
  }

  navigateByHttpLink(page: string) {
    this.ssoService.openExternalLink(page);
  }

  prepareMenuItems(items: MenuItem[]): MenuItem[] {
    return items.map(item => ({
      ...item,
      children: this.prepareMenuItems(item.children),
      opened: item.children.length && this.router.isActive(item.link, false)
    }));
  }

  prepareExternalLinks(items: MenuItem[]) {
    return items.find(item => item.code === 'business').children;
  }

  close() {
    this.closed.emit();
  }

  logout() {
    this.store.dispatch(new DirectLoggedOut());
    // this.router.navigate(['/login']);
  }

  toggleSubmenu(item: MenuItem): boolean {
    if (!item.children.length) {
      return false;
    }
    item.opened = !item.opened;
    this.submenuOpened = this.menuItems.some(({ opened }) => opened);
    return true;
  }

  linkIsActive(item: MenuItem): boolean {
    return item.item_type !== 'external_link' && this.router.isActive(item.link, false);
  }

  menuItemTrackBy(item: MenuItem): string {
    return item.code;
  }
}
